import { Box, Grid, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, Paper, Menu, MenuItem, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, TablePagination, Chip, Autocomplete, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import axios from 'axios';
import { API_PATH, appContext } from '../../../App';
import { MachineInfo, MachineModelInfo, CustomerGroup, Pageable, SelectedDataItem } from './types';
import MultipleSelectChip from '../GlobalComponents/MultiSelectChip';
import SingleSelectStringChip from '../GlobalComponents/SingleSelectStringChip';
import ConfigModal from './ConfigModal';
import TableLoader from '../GlobalComponents/TableLoader';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryModal from '../GlobalComponents/HistoryModal';
import SearchIcon from '@mui/icons-material/Search'
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Tooltip from '@mui/material/Tooltip';
import { Invoice, Machine } from '../Invoices/types';
import { InfoOutlined, Description, Download } from '@mui/icons-material';
import InvoiceModal from '../Invoices/InvoiceModal';
import Cookies from 'js-cookie';
import { EmitterHash } from '../Monitor/types';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';


interface Props { }

function MachineViewList(props: Props) {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [customerIds, setCustomerIds] = useState<number[]>([]);
    const [modelIds, setModelIds] = useState<number[]>([]);
    const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
    const [modelDataList, setModelDataList] = useState<SelectedDataItem[]>([]);
    const [customerDataList, setCustomerDataList] = useState<SelectedDataItem[]>([]);
    const [invoiceId, setInvoiceId] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(300);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [modalData, setModalData] = useState<MachineInfo[]>([]);
    const [paginatedDataList, setPaginatedDataList] = useState<MachineInfo[]>([]);
    const [totalItemCount, setTotalItemCount] = useState<number>(0);
    const [pageableData, setPageableData] = useState<Pageable | null>(null);
    const [statusList, setStatusList] = useState<string[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<string | null>(null);
    const [setupTypeFilter, setSetupTypeFilter] = useState<string | null>(null);
    const [selectedRows, setSelectedRows] = useState<MachineInfo[]>([]);
    const [trigger, setTrigger] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedMachine, setSelectedMachine] = useState<MachineInfo>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
    const [machineList, setMachineList] = useState<Machine[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [trigerList, setTriggerList] = useState<boolean>(true)
    const [hashEmitterList, setHashEmitterList] = useState<string[]>([])
    const [activeCloudOrders, setActiveCloudOrders] = useState<String[]>([])
    const [selectedInvoiceType, setSelectedInvoiceType] = useState<string | null>(null);
    const [invoiceTypeList, setInvoiceTypeList] = useState<string[]>([]);
    const [resellerList, setResellerList] = useState<{ id: number, resellerName: string }[]>([]);
    const [billingScheme, setBillingScheme] = useState<string | null>(null);
    const [machineType, setMachineType] = useState<string | null>(null);
    const { authRole } = React.useContext(appContext);


    const handleCloseModal = () => {
        setOpenModal(false);
        setModalData([]);
    };

    useEffect(() => {
        fetchActiveCloudOrderList();
        fetchMachineList();
        fetchCustomerList();
        fetchStateList();
        fetchInvoiceTypeList();
        axios.get(API_PATH + '/customer_ledger/allResellers', getAxiosConfig())
            .then(response => setResellerList(response.data))
            .catch(error => console.error("Error fetching resellers:", error));
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            let hashList = hashEmitterList;
            if (!hashList || hashList.length === 0) {
                hashList = await fetchEmitterList();
            }
            fetchTableData(hashList);
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger, page, rowsPerPage]);

    const fetchEmitterList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(API_PATH + "/monitor/emitterhash", getAxiosConfig());
            const emitterHashData: EmitterHash[] = response.data;
            const allWorkers = emitterHashData.flatMap((item) => item.workerList || []);
            setHashEmitterList(allWorkers);
            return allWorkers;
        } catch (error) {
            console.error("Error fetching emitter hash list:", error);
            return [];
        }
    };

    const fetchActiveCloudOrderList = async () => {
        try {
            const response = await axios.get(API_PATH + '/machines/orders/cloud_order_workers', getAxiosConfig());
            setActiveCloudOrders(response.data);
        } catch (error) {
            console.error("Error fetching active cloud order list:", error);
        }
    };

    const fetchTableData = async (hashList: string[]) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${API_PATH}/machines/list?key=${searchTerm.replace(/\+/g, "%2B")}&customer=${customerIds.join(',')}&model=${modelIds.join(',')}&state=${selectedStatus}&setupType=${setupTypeFilter || ''}&invoiceId=${invoiceId}&page=${page}&limit=${rowsPerPage}&invoiceType=${selectedInvoiceType || ''}&billingScheme=${billingScheme || ''}&machineType=${machineType || ''}`,
                getAxiosConfig()
            );

            const data = response.data.content;
            const paginatedData = data.map((item: any) => ({
                id: item.id,
                workerId: item.workerId,
                invoice: item.invoice,
                invoiceId: item.invoice.id,
                reference: item.invoice.reference,
                customerGroupName: item.customer.customerGroupName,
                modelName: item.machineModel.modelName,
                machineType: item.machineType,
                virtualHashRate: item.virtualHashRate,
                hashUnit: item.machineModel.hashUnit,
                targetPool: item.targetPool,
                hashVendor: item.hashVendor,
                machineStatus: item.machineStatus,
                poolStatus: item.poolStatus,
                data: item,
                hostingCharge: item.invoiceItem.hostingCharge,
                hostingBillingScheme: item.hostingBillingScheme,
                onStatus: hashList.includes(item.workerId),
                isCloudOrderActive: activeCloudOrders.includes(item.workerId),
                organization: item.organization,
                resellerId: item.resellerId,
                resellerName: item.resellerName,
            }));

            setPaginatedDataList(paginatedData);
            setTotalItemCount(response.data.totalElements);
            setPageableData({
                pageNumber: response.data.pageable.pageNumber,
                pageSize: response.data.pageable.pageSize,
            });
            setSelectedRows([]);
        } catch (error) {
            console.error("Error fetching machine list:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMachineList = () => {
        axios.get(API_PATH + '/machine_model/list', getAxiosConfig())
            .then(response => {
                const DataList: SelectedDataItem[] = response.data.map((item: MachineModelInfo) => ({
                    id: item.id,
                    name: item.modelName
                }));
                setModelDataList(DataList);
                setMachineList(response.data);
            })
            .catch(error => {
                console.error("Error fetching machine list:", error);
            });
    };

    const fetchCustomerList = () => {
        axios.get(API_PATH + '/customer/list?orderBy=none', getAxiosConfig())
            .then(response => {
                const DataList: SelectedDataItem[] = response.data.map((item: CustomerGroup) => ({
                    id: item.id,
                    name: item.customerGroupName
                }));
                setCustomerDataList(DataList);
            })
            .catch(error => {
                console.error("Error fetching customer list:", error);
            });
    };

    const fetchStateList = () => {
        axios.get(API_PATH + '/machines/states', getAxiosConfig())
            .then(response => {
                setStatusList(response.data);
            })
            .catch(error => {
                console.error("Error fetching states list:", error);
            });
    };

    const fetchInvoiceTypeList = () => {
        axios.get(API_PATH + '/machines/invoice_types', getAxiosConfig())
            .then(response => {
                setInvoiceTypeList(response.data);
            })
            .catch(error => {
                console.error("Error fetching invoice types list:", error);
            });
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSearchButtonClick = () => {
        setHashEmitterList([]);
        setPage(0);
        setTrigger(!trigger);
    };


    const handleCloseConfirmation = (confirm: boolean) => {
        setOpenConfirmation(false);
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSelectRow = (id: number) => {
        setSelectedRows((prev) => {
            const isSelected = prev.some((row) => row.id === id);
            if (isSelected) {
                return prev.filter((row) => row.id !== id);
            } else {
                const selectedRow = paginatedDataList.find((row) => row.id === id);
                return selectedRow ? [...prev, selectedRow] : prev;
            }
        });
    };
    const handleCommonConfig = () => {
        if (selectedRows.length > 0) {
            setModalData(selectedRows);
            setOpenModal(true);
        }
    };

    const handleSelectAll = () => {
        if (selectedRows.length === paginatedDataList.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedDataList);
        }
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, machine: MachineInfo) => {
        setAnchorEl(event.currentTarget);
        setSelectedMachine(machine);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleHistoryOpen = () => {
        setHistoryModalOpen(true);
        handleMenuClose();
    };

    const handleHistoryClose = () => setHistoryModalOpen(false);

    function getPoolStatus(poolStatus: string): React.ReactNode {
        if (poolStatus === null || poolStatus.trim().length === 0) return <></>;
        if (poolStatus === "ACTIVE") return <Tooltip title="Active in pool">
            <PowerIcon fontSize='small' sx={{ color: "#0F0" }} />
        </Tooltip>;
        if (poolStatus === "INACTIVE") return <Tooltip title="Inactive in pool">
            <PowerOffIcon fontSize='small' sx={{ color: "#F00" }} />
        </Tooltip>;
        return <Tooltip title="Missing in pool">
            <ReportProblemIcon fontSize='small' sx={{ color: '#ff5722' }} />
        </Tooltip>;
    }

    const handleRowClick = (customerGroupName: string, invoiceId: number) => {
        axios.get(`${API_PATH}/invoice/list?key=${customerGroupName}&page=${0}&limit=${100}`, getAxiosConfig())
            .then(response => {
                response.data.content && setSelectedInvoice(response.data.content.find((i: Invoice) => i.id === invoiceId));
            })
            .catch(error => {
                console.error("Error fetching invoice list:", error);
            })
        handleMenuClose();
    };

    const handleCloseInvoiceModal = () => {
        setSelectedInvoice(null);
    }

    const handleDownloadCSV = () => {
        axios.get(`${API_PATH}/machines/list?key=${searchTerm.replace(/\+/g, "%2B")}&customer=${customerIds.join(',')}&model=${modelIds.join(',')}&state=${selectedStatus}&invoiceId=${invoiceId}&page=${page}&limit=${totalItemCount}&billingScheme=${billingScheme || ''}&machineType=${machineType || ''}`, getAxiosConfig())
            .then(response => {
                const paginatedDataList = response?.data?.content || [];
                const headers = [
                    'ID',
                    'Worker ID',
                    'Customer Name',
                    'Model Name',
                    'Target Pool',
                    'Hash Vendor',
                    'Hosting Charge',
                    'Billing Scheme',
                    'Pool Status',
                    'State',
                ];

                const csvRows = [
                    headers.join(','),
                    ...paginatedDataList.map((item: any) => [
                        item?.id,
                        item?.workerId,
                        item?.customer?.customerGroupName,
                        item?.machineModel?.modelName,
                        item?.targetPool,
                        item?.hashVendor,
                        item?.invoiceItem?.hostingCharge,
                        item?.hostingBillingScheme || 'MONTHLY',
                        (item?.targetPool === "F2POOL" && item?.hashVendor === "NICEHASH") ?
                            (hashEmitterList.includes(item.workerId) ? "ACTIVE" : "INACTIVE") :
                            item?.poolStatus,
                        (item?.targetPool === "F2POOL" && item?.hashVendor === "NICEHASH") ?
                            (hashEmitterList.includes(item.workerId) ? "ON" : "OFF") :
                            item?.machineStatus,
                    ].join(',')),
                ];
                const csvString = csvRows.join('\n');
                const blob = new Blob([csvString], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'machine_report.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error('Error fetching data for CSV:', error);
            });
    };


    return (
        <Box sx={{ flexGrow: 1, p: 2, position: 'relative', height: 'calc(100vh - 64px)' }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8} lg={9}>
                    <TextField
                        label="Search"
                        value={searchTerm}
                        onChange={handleSearch}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Search by worker ID, invoice reference..."
                    />
                </Grid>

                <Grid item xs={12} md={4} lg={3}>
                    <Grid container spacing={1}>
                        <Grid item xs={8}>
                            <TextField
                                label="Invoice ID"
                                value={invoiceId}
                                onChange={(e) => setInvoiceId(e.target.value)}
                                variant="outlined"
                                size="small"
                                type="number"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearchButtonClick}
                                fullWidth
                                startIcon={<SearchIcon />}
                                sx={{ height: '40px' }}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={0}>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Autocomplete
                        multiple
                        options={customerDataList}
                        getOptionLabel={(option) => option.name}
                        value={customerDataList.filter(item => customerIds.includes(item.id))}
                        onChange={(event, newValue) => {
                            setCustomerIds(newValue.map(item => item.id));
                        }}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Customer"
                                placeholder="Type to search..."
                                size="small"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    size="small"
                                />
                            ))
                        }
                        sx={{ width: '100%', mx: 1 }}
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Autocomplete
                        multiple
                        options={modelDataList}
                        getOptionLabel={(option) => option.name}
                        value={modelDataList.filter(item => modelIds.includes(item.id))}
                        onChange={(event, newValue) => {
                            setModelIds(newValue.map(item => item.id));
                        }}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model"
                                placeholder="Type to search..."
                                size="small"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    size="small"
                                />
                            ))
                        }
                        sx={{ width: '100%', mx: 1 }}
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <SingleSelectStringChip
                        dataList={statusList}
                        selectedValue={selectedStatus}
                        setSelectedValue={setSelectedStatus}
                        name="Status"
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <SingleSelectStringChip
                        dataList={['AIR', 'HYDRO']}
                        selectedValue={setupTypeFilter}
                        setSelectedValue={setSetupTypeFilter}
                        name="Setup Type"
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <SingleSelectStringChip
                        dataList={invoiceTypeList}
                        selectedValue={selectedInvoiceType}
                        setSelectedValue={setSelectedInvoiceType}
                        name="Invoice Type"
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <SingleSelectStringChip
                        dataList={['MONTHLY', 'PREPAID', 'AUTO_SETTLEMENT']}
                        selectedValue={billingScheme}
                        setSelectedValue={setBillingScheme}
                        name="Billing Scheme"
                    />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <SingleSelectStringChip
                        dataList={['Fractional', 'Whole']}
                        selectedValue={machineType}
                        setSelectedValue={setMachineType}
                        name="Machine Type"
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={3}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleDownloadCSV}
                        disabled={hashEmitterList.length === 0}
                    >
                        <Download />
                    </Button>

                    {authRole === 'ADMIN' && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCommonConfig}
                            disabled={selectedRows.length === 0}
                            startIcon={<SettingsIcon />}
                            fullWidth
                            sx={{ justifyContent: 'space-between' }}
                        >
                            Configure Selected
                            <Box
                                sx={{
                                    backgroundColor: selectedRows.length === 0
                                        ? 'rgba(255,255,255,0.3)'
                                        : 'rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    px: 1,
                                    py: 0.25,
                                    ml: 1,
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    minWidth: '24px',
                                    display: 'inline-flex',
                                    justifyContent: 'center'
                                }}
                            >
                                {selectedRows.length}
                            </Box>
                        </Button>
                    )}
                </Grid>
            </Grid>

            <Paper elevation={3} sx={{ padding: 3, marginTop: 2 }}>
                {loading ? (
                    <TableLoader />
                ) : (
                    <Table style={{ width: '100%' }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }}>
                                {authRole === 'ADMIN' &&
                                    <TableCell style={{ width: '5%' }}>
                                        <Checkbox
                                            checked={selectedRows.length === paginatedDataList.length}
                                            onChange={() => handleSelectAll()}
                                        />
                                    </TableCell>
                                }
                                <TableCell>ID</TableCell>
                                <TableCell>&nbsp;</TableCell>
                                <TableCell>Worker ID</TableCell>
                                <TableCell>Customer Name</TableCell>
                                <TableCell>Model Name</TableCell>
                                <TableCell>Target Pool</TableCell>
                                <TableCell>Hash Vendor</TableCell>
                                <TableCell>Hosting Charge</TableCell>
                                <TableCell>Billing Scheme</TableCell>
                                <TableCell>Pool Status</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedDataList.map((item) => (
                                <TableRow key={item.id}>
                                    {authRole === 'ADMIN' && (
                                        <TableCell style={{ width: '5%' }}>
                                            <Checkbox
                                                checked={selectedRows.some((row) => row.id === item.id)}
                                                onChange={() => handleSelectRow(item.id)}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>
                                        {(item.targetPool === "F2POOL" && item.hashVendor === "NICEHASH") ? (
                                            <span
                                                style={{
                                                    color: item?.onStatus ? "#66bb6a" : "#e57373",
                                                    fontWeight: "bold",
                                                    fontSize: "0.8rem"
                                                }}
                                            >
                                                {item?.onStatus ? "" : getPoolStatus(item?.poolStatus)}
                                            </span>
                                        ) : (
                                            <span>{getPoolStatus(item?.poolStatus)}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>{item.workerId}</TableCell>
                                    <TableCell>
                                        <div>{item.customerGroupName}</div>
                                        {(item.resellerName || item.resellerId) && (
                                            <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 0.5 }}>
                                                Affiliate: {item.resellerName || (item.resellerId ? resellerList.find(r => r.id === item.resellerId)?.resellerName : '')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip
                                            placement="right-start"
                                            title={
                                                (() => {
                                                    const matchedMachine = machineList.find(machine => machine.modelName === item.modelName);
                                                    return matchedMachine ? (
                                                        <>
                                                            Model: {matchedMachine.modelName} <br />
                                                            Coin: {matchedMachine.coin.coin} <br />
                                                            Algorithm: {matchedMachine.coin.algorithm} <br />
                                                            Hash Rate: {matchedMachine.hashRate} {matchedMachine.hashUnit} <br />
                                                            Power: {matchedMachine.power}W <br />
                                                            Price: ${matchedMachine.price} <br />
                                                            Vendor: {matchedMachine.vendor.vendorName} <br />
                                                            Setup Type: {matchedMachine.setupType || 'N/A'}
                                                        </>
                                                    ) : (
                                                        'No details available'
                                                    );
                                                })()
                                            }
                                            arrow
                                        >
                                            <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                {item.modelName}
                                                {item.machineType === 'VIRTUAL' && (
                                                    <Chip
                                                        label={`${item.virtualHashRate ?? ''} ${item.hashUnit || ''} virtual`}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        style={{ marginLeft: '6px' }}
                                                    />
                                                )}
                                                <InfoOutlined fontSize="small" style={{ marginLeft: '4px', color: '#1976D2' }} />
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell>{item.targetPool}</TableCell>

                                    <TableCell>{item.hashVendor}</TableCell>
                                    <TableCell>{item.hostingCharge}</TableCell>
                                    <TableCell>{item.hostingBillingScheme || 'MONTHLY'}</TableCell>
                                    <TableCell>
                                        {item.isCloudOrderActive ? (
                                            <span
                                                style={{
                                                    color: "#66bb6a",
                                                    fontWeight: "bold",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}
                                            >
                                                ACTIVE

                                                <Tooltip title="Cloud order machine" arrow>
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            backgroundColor: "#e0f4f8",
                                                            border: "1px solid #00819bff",
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        <ElectricalServicesIcon
                                                            fontSize="small"
                                                            sx={{ color: "#00819bff" }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </span>
                                        ) : (item.targetPool === "F2POOL" && item.hashVendor === "NICEHASH") ? (
                                            <span style={{
                                                color: item.onStatus ? "#66bb6a" : "#e57373",
                                                fontWeight: "bold",
                                                fontSize: "0.8rem"
                                            }}>
                                                {item.onStatus ? "ACTIVE" : "INACTIVE"}
                                            </span>
                                        ) : (
                                            <span style={{
                                                color: item.poolStatus === "ACTIVE" ? "#66bb6a" : "#e57373",
                                                fontWeight: "bold",
                                                fontSize: "0.8rem"
                                            }}>
                                                {item.poolStatus}
                                            </span>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        {item.isCloudOrderActive ? (
                                            <span
                                                style={{
                                                    color: "#66bb6a",
                                                    fontWeight: "bold",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}
                                            >
                                                ON
                                                <Tooltip title="Cloud order machine" arrow>
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            backgroundColor: "#e0f4f8",
                                                            border: "1px solid #00819bff",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <ElectricalServicesIcon
                                                            fontSize="small"
                                                            sx={{ color: "#00819bff" }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </span>
                                        ) : (item.targetPool === "F2POOL" && item.hashVendor === "NICEHASH") ? (
                                            <span style={{ color: item?.onStatus ? "green" : "red", fontWeight: "bold" }}>
                                                {item?.onStatus ? "ON" : "OFF"}
                                            </span>
                                        ) : (
                                            <span style={{ color: item.machineStatus === "ON" ? "green" : "red", fontWeight: "bold" }}>
                                                {item.machineStatus}
                                            </span>
                                        )}
                                    </TableCell>

                                    {authRole === 'ADMIN' &&
                                        <TableCell>
                                            <IconButton
                                                onClick={(event) => handleMenuClick(event, item)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl) && selectedMachine === item}
                                                onClose={handleMenuClose}
                                            >
                                                <MenuItem onClick={handleHistoryOpen}>
                                                    <HistoryIcon sx={{ mr: 1 }} /> History
                                                </MenuItem>
                                                <MenuItem onClick={() => handleRowClick(item.customerGroupName, item.invoiceId)}>
                                                    <Description sx={{ mr: 1 }} />Invoice
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    }
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                <TablePagination
                    rowsPerPageOptions={[300, 500, 1000, 1500]}
                    component="div"
                    count={totalItemCount}
                    rowsPerPage={pageableData?.pageSize || 300}
                    page={pageableData?.pageNumber || 0}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </Paper>
            <ConfigModal
                open={openModal}
                onClose={handleCloseModal}
                data={modalData}
                trigger={trigger}
                setTrigger={setTrigger}
            />
            <HistoryModal
                open={historyModalOpen}
                onClose={handleHistoryClose}
                selectedId={selectedMachine?.id || null}
                entity={'Machines'}
            />
            <InvoiceModal
                open={Boolean(selectedInvoice)}
                isAdding={false}
                onClose={handleCloseInvoiceModal}
                selectedInvoice={selectedInvoice}
                setSelectedInvoice={setSelectedInvoice}
                setTriggerList={setTriggerList}
                trigerList={trigerList}
            />
            <Dialog
                open={openConfirmation}
                onClose={() => handleCloseConfirmation(false)}
            >
                <DialogTitle>Confirm Status Change</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change the status?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseConfirmation(false)}>Cancel</Button>
                    <Button onClick={() => handleCloseConfirmation(true)} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MachineViewList;